import { Nullable, PartialNullable } from "@seriously-rad/seriouslyrad-framework-core/Extensions";
import { ArrayHelper } from "@seriously-rad/seriouslyrad-framework-core/Helpers/ArrayHelper";
import { ObjectHelper } from "@seriously-rad/seriouslyrad-framework-core/Helpers/ObjectHelper";
import { PropertyChange } from "@seriously-rad/seriouslyrad-framework-core/PropertyChange";
import { Property, PropertyBoolean } from "@seriously-rad/seriouslyrad-framework-core/RadObject";
import { AjaxError, IValidationError } from "@seriously-rad/seriouslyrad-framework-core/Server/AjaxError";
import { TrueEvaluator } from "@seriously-rad/seriouslyrad-framework-core/Server/DotNet/SeriouslyRAD.Framework.Data.Entities/Evaluation/Evaluators/TrueEvaluator";
import { WebApiClientService } from "@seriously-rad/seriouslyrad-framework-core/Server/WebApiClientService";
import { ServiceManager } from "@seriously-rad/seriouslyrad-framework-core/ServiceManager";
import { ButtonStyleEnum } from "@seriously-rad/seriouslyrad-framework-core/Ui/Controls/ButtonStyleEnum";
import { ControlRef } from "@seriously-rad/seriouslyrad-framework-core/Ui/Controls/ControlRef";
import { Dialog, DialogPropertyValues, IDialog } from "@seriously-rad/seriouslyrad-framework-core/Ui/Controls/Dialog";
import { ErrorDialog } from "@seriously-rad/seriouslyrad-framework-core/Ui/Controls/ErrorDialog";
import { PropertyEditor } from "@seriously-rad/seriouslyrad-framework-core/Ui/Controls/PropertyEditor";
import { PropertyEditorTextBoxPassword } from "@seriously-rad/seriouslyrad-framework-core/Ui/Controls/PropertyEditorTextBoxPassword";
import { ValidationPanel } from "@seriously-rad/seriouslyrad-framework-core/Ui/Controls/ValidationPanel";
import { DialogResultEnum } from "@seriously-rad/seriouslyrad-framework-core/Ui/Dialogs/DialogResultEnum";
import { NAMESPACE } from "./_Namespace";

export type SetUserPasswordDialogPropertyValues =
    DialogPropertyValues
    & {
        isSaving: boolean;
    };

export type SetUserPasswordDialogProperties = {
    [name in keyof SetUserPasswordDialogPropertyValues]: Property<SetUserPasswordDialogPropertyValues[name]>
};

export interface ISetUserPasswordDialog extends IDialog, SetUserPasswordDialogProperties
{
}

export class SetUserPasswordDialog extends Dialog implements ISetUserPasswordDialog
{
    // #region Static

    public static override classNamespace = NAMESPACE + "SetUserPasswordDialog";
    public static override className = "SetUserPasswordDialog";

    // #region Overrides

    // #region Methods

    public static override createPropertyDefaults(): PartialNullable<SetUserPasswordDialogPropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
                buttonStyle: ButtonStyleEnum.okCancel,
                isSaving: false,
                title: "Set User Password",
            }
        };
    }

    // #endregion

    // #endregion

    // #endregion

    // #region Instance

    // #region Global Variables

    protected userId?: string;
    protected validationPanelControlRef?: ControlRef<ValidationPanel>;

    // #endregion

    // #region Properties

    public isSaving!: PropertyBoolean;

    // #endregion

    // #region Constructors

    constructor(propertyValues?: PartialNullable<SetUserPasswordDialogPropertyValues>)
    {
        super(propertyValues);
    }

    // #endregion

    // #region Public Methods

    public closeAsyncSuper()
    {
        return super.closeAsync();
    }
    public resetValidation()
    {
        this.controlsGet(true)
            .filter(item => item instanceof PropertyEditor)
            .forEach(item => { item.errorState.set(false); });

        this.validationPanelControlRef?.current?.hide();
    }
    public setValidation(validationErrors: Nullable<IValidationError[]>)
    {
        const
            self = this;

        self.resetValidation();

        if (!validationErrors)
            return;

        if (validationErrors.length === 0)
            return;

        const
            errorMessages: string[] = [],
            controls = self.controlsGet(true);

        validationErrors.forEach(item =>
        {
            controls
                .filter(control => control instanceof PropertyEditor)
                .filter(control => ArrayHelper.contains(item.propertyName.toLowerCase().split(","), control.dataSourceProperty.get()?.toLowerCase()))
                .forEach(control => { control.errorState.set(true); });

            errorMessages.push(item.errorMessage);
        });

        self.validationPanelControlRef?.current?.setErrorMessages(errorMessages);
    }

    // #endregion

    // #region Protected Methods

    protected _isSavingChanged(change: PropertyChange<boolean>)
    {
        /* Intentionally empty */
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: SetUserPasswordDialogPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this.isSaving = new PropertyBoolean(this, propertyValues.isSaving, this._isSavingChanged);
    }

    protected override _createChildrenHtml()
    {
        return (
            <>
                <ValidationPanel ref={this.validationPanelControlRef} />
                <PropertyEditorTextBoxPassword dataSourceProperty="password" dataSourcePropertyType="System.String, System.Private.CoreLib" propertyDisplayName="New Password" mandatoryEvaluator={new TrueEvaluator()} />
                <PropertyEditorTextBoxPassword dataSourceProperty="confirmPassword" dataSourcePropertyType="System.String, System.Private.CoreLib" propertyDisplayName="Confirm Password" mandatoryEvaluator={new TrueEvaluator()} />
            </>
        );
    }

    public override closeAsync()
    {
        const
            self = this;

        self.resetValidation();

        if (self.dialogResult !== DialogResultEnum.ok)
            return self.closeAsyncSuper();

        const
            data = ObjectHelper.cloneObject(self.dataSource.get()?.data.get() ?? {});

        data.id = self.userId;

        self.isSaving.set(true);

        return new Promise((resolve, reject) =>
        {
            ServiceManager.getRequired(WebApiClientService)
                .createApiMethodRequest("DataService", "CallDataService", {
                    id: "51979B2D-CA0A-4C12-A793-BB6BF35F4B1D",
                    operation: "SetUserPassword",
                    data: ObjectHelper.toJson(data)
                })
                .executeAsync()
                .then(() =>
                {
                    self.closeAsyncSuper()
                        .then(() => { resolve(undefined); })
                        .catch(error => { reject(error); });
                })
                .catch(error =>
                {
                    if (error instanceof AjaxError && error.type.get() === "ValidationApiException")
                    {
                        self.setValidation(error.validationErrors.get());
                    }

                    if (error?.type === "ValidationApiException")
                        self.setValidation(error.validationErrors);
                    else
                        ErrorDialog.displayError(error);

                    reject(error);
                })
                .finally(() =>
                {
                    self.isSaving.set(false);
                });
        });
    }
    public override openAsync(userId: string)
    {
        this.userId = userId;

        // Stopped working: this.trigger("editModeChanged", true);

        const
            controls = this.controlsGet(true);

        controls
            .filter(control => control instanceof PropertyEditor)
            .forEach(control => { control.editMode.set(true); });

        return super.openAsync();
    }

    // #endregion

    // #endregion
}
